import { ref } from 'vue';
import { api } from '../api.js';

/**
 * Org-scoped "App settings" — the persistent DEFAULT pins.
 *
 * Reuses the existing `containerAttrs` org setting (semantic rename in the UI
 * only; storage key + routes unchanged). A fresh session with no `pins` in the
 * URL seeds its session pins from these defaults (see LogsView); the App
 * settings page edits them directly via the atomic per-container route so two
 * editors on two different containers can't clobber each other.
 */

/** Columns are bounded so the list header/row grid never explodes (Codex
 * amendment): ≤6 pinned-attr columns on desktop, else fall back to the
 * mobile-style labeled line even on wide screens. */
export const MAX_PIN_COLUMNS = 6;

/**
 * Flatten a session-pins object into the deterministic, distinct list of attr
 * keys to surface as columns. Pure so it is trivially unit-testable.
 *
 * - `containers` is the current container filter; only pins for those
 *   containers count. Empty filter = "all containers" (every pinned key).
 * - Order is INSERTION order — containers in object order, keys in array order —
 *   so columns stay stable across polling/pagination (they derive from the pins
 *   STATE, not from whatever rows happen to be visible).
 * - Distinct: a key pinned on two relevant containers appears once.
 */
export function deriveColumnKeys(
  pins: Record<string, string[]> | null | undefined,
  containers: string[],
): string[] {
  if (!pins) return [];
  const relevant = containers.length ? new Set(containers) : null;
  const keys: string[] = [];
  for (const [container, ks] of Object.entries(pins)) {
    if (relevant && !relevant.has(container)) continue;
    if (!Array.isArray(ks)) continue;
    for (const k of ks) {
      if (typeof k === 'string' && k && !keys.includes(k)) keys.push(k);
    }
  }
  return keys;
}

export function useAppSettings() {
  const defaultPins = ref<Record<string, string[]>>({});
  const loaded = ref(false);

  // Guards a slow initial GET landing after a save has already applied a newer
  // optimistic value (same pattern as useUserSettings).
  let loadSeq = 0;

  async function loadDefaultPins(): Promise<void> {
    const seq = ++loadSeq;
    try {
      const value = await api.getContainerAttrs();
      if (seq !== loadSeq) return;
      defaultPins.value = value;
    } catch {
      if (seq !== loadSeq) return;
      // Leave whatever we had — a fresh session just won't seed columns.
    } finally {
      if (seq === loadSeq) loaded.value = true;
    }
  }

  /** Atomic per-container write of the default pin keys. Optimistic; rolls back
   * and rethrows on failure so callers can surface it. */
  async function setContainerDefaultPins(container: string, keys: string[]): Promise<void> {
    loadSeq++; // a mutation always supersedes an in-flight load
    const previous = defaultPins.value;
    const next = { ...previous };
    if (keys.length) next[container] = keys;
    else delete next[container];
    defaultPins.value = next; // optimistic
    try {
      await api.putContainerAttrKeys(container, keys);
    } catch (e) {
      defaultPins.value = previous; // roll back on failure
      throw e;
    }
  }

  return { defaultPins, loaded, loadDefaultPins, setContainerDefaultPins };
}
